import { findDanglingReferences } from "../infra/audit/store";
import type { MarlinStoreDb } from "../infra/db";
import {
  getAllSites,
  purgeMachineReferences,
  replaceSites,
} from "../sites/machines";
import { pruneLayoutReferences } from "../logic/layouts/references";
import { getScreenLayouts } from "../screens/store";
import { getDatarouterConfig, getIpJsonContent } from "../settings/store";
import type { ScreenLayout, SingleSite } from "../types";

export const SYNCED_AT_KEY = "cloud.syncedAt";

export type CloudConfiguration = Partial<{
  screenLayouts: ScreenLayout[];
  machines: Record<number, SingleSite>;
  datarouter: unknown;
  ipJson: unknown;
  syncedAt: string;
}>;

export const exportConfiguration = async (
  db: MarlinStoreDb,
): Promise<Required<CloudConfiguration>> => ({
  screenLayouts: await getScreenLayouts(db),
  machines: await getAllSites(db),
  datarouter: await getDatarouterConfig(db),
  ipJson: await getIpJsonContent(db),
  syncedAt: new Date().toISOString(),
});

export const markSynced = async (
  db: MarlinStoreDb,
  syncedAt: string,
): Promise<void> => {
  await db.settings.put({ key: SYNCED_AT_KEY, value: syncedAt });
};

export const importConfiguration = async (
  db: MarlinStoreDb,
  config: CloudConfiguration,
): Promise<{ dropped: number[] }> => {
  const dropped: number[] = [];
  await db.transaction("rw", db.tables, async () => {
    const has = (key: keyof CloudConfiguration) =>
      Object.prototype.hasOwnProperty.call(config, key);
    const droppedSites = new Set<number>();
    if (has("machines")) {
      const sites = Object.values(config.machines ?? {});
      const keptMachines = new Set(sites.map((site) => site.machine_id));
      const keptSites = new Set(sites.map((site) => site.site_id));
      for (const local of await db.machines.toArray()) {
        if (!local.machine_id || keptMachines.has(local.machine_id)) continue;
        if (!dropped.includes(local.machine_id)) dropped.push(local.machine_id);
        if (!keptSites.has(local.site_id)) droppedSites.add(local.site_id);
      }
      await replaceSites(db, sites);
    }
    if (has("screenLayouts")) {
      await db.screens.clear();
      const knownSites = new Set(
        (await db.machines.toArray()).map((machine) => machine.site_id),
      );
      const layouts = config.screenLayouts ?? [];
      for (const layout of layouts) {
        pruneLayoutReferences(layout, {
          keepMachine: (id) => !dropped.includes(id),
          keepSite: (id) => knownSites.has(id),
        });
      }
      await db.screens.bulkPut(layouts);
    }
    if (has("datarouter"))
      await db.settings.put({ key: "datarouter", value: config.datarouter });
    if (has("ipJson"))
      await db.settings.put({ key: "ipJson", value: config.ipJson });
    if (has("syncedAt"))
      await db.settings.put({ key: SYNCED_AT_KEY, value: config.syncedAt });
    for (const machineId of dropped)
      await purgeMachineReferences(db, machineId, droppedSites);

    const dangling = (await findDanglingReferences(db)).filter((reference) =>
      ["screens", "selection", "offsets"].includes(reference.table),
    );
    if (dangling.length > 0)
      throw new Error(
        `import produced dangling references: ${JSON.stringify(dangling)}`,
      );
  });
  return { dropped };
};

export const getSyncedAt = async (
  db: MarlinStoreDb,
): Promise<string | undefined> =>
  (await db.settings.get(SYNCED_AT_KEY))?.value as string | undefined;
